import type { AdminTicketDetailsDto, UserDto } from "@/api/types";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { ModalBodySpinner } from "@/design-system/Spinner";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { ContentFieldProvider } from "@/providers/ContentFieldProvider";

import type { TicketCommentType } from "./TicketCommentField/helpers";
import { TicketCommentFieldExtended } from "./TicketCommentField/TicketCommentFieldExtended";

type TicketLeaveCommentModalProps = ModalBaseProps & {
  ticket: AdminTicketDetailsDto;
  title: React.ReactNode;
  description: React.ReactNode;
  canCommentInternal: boolean;
  canCommentPublic: boolean;
  assignee?: UserDto;
  onSubmit: (value: string, isInternal: boolean) => void | Promise<void>;
};

export function TicketLeaveCommentModal({
  ticket,
  title,
  description,
  isOpened,
  canCommentInternal,
  canCommentPublic,
  onOpenChange,
  onSubmit,
}: TicketLeaveCommentModalProps): React.ReactNode {
  const [submitting, submittingHandlers] = useBool();
  const sessionUser = useSessionUser();

  async function handleSubmit({ note, isInternal = false }: { note: string; isInternal?: boolean }) {
    if (submitting) {
      return;
    }

    try {
      submittingHandlers.setTrue();

      if (note) {
        await onSubmit?.(note, isInternal);
      } else {
        onOpenChange(false);
      }
    } finally {
      submittingHandlers.setFalse();
    }
  }

  const allowedCommentTypes: TicketCommentType[] = [];
  if (canCommentPublic) allowedCommentTypes.push("public");
  if (canCommentInternal) allowedCommentTypes.push("internal");

  return (
    <Modal.Root data-testid="leave-comment-modal" isNested {...{ title, description, isOpened, onOpenChange }}>
      <Modal.Body>
        {submitting && <ModalBodySpinner />}
        {!submitting && allowedCommentTypes.length > 0 && (
          <ContentFieldProvider>
            <TicketCommentFieldExtended
              ticket={ticket}
              allowedCommentTypes={allowedCommentTypes}
              isAttachmentAllowed={false}
              user={sessionUser}
              onComment={(payload) => handleSubmit({ note: payload.content, isInternal: payload.type === "internal" })}
            />
          </ContentFieldProvider>
        )}
      </Modal.Body>
    </Modal.Root>
  );
}
